export const OTHER_PREFIX = 'Other: '

const BULLET_PREFIX = /^[-•]\s*/
const BULLETS = '-•'
const WHITESPACE = /\s/

// Drop the bullet and surrounding whitespace a saved (newline) answer carries.
function stripLeading(text: string): string {
  return text.trimStart().replace(BULLET_PREFIX, '')
}

// True when a label ending at `end` stops at a real delimiter, so "Sample" is
// not matched out of "Sampletown revitalisation".
function endsAtDelimiter(text: string, end: number): boolean {
  let i = end
  while (i < text.length && WHITESPACE.test(text[i])) i += 1
  return i === text.length || text[i] === ','
}

// The longest option label appearing at `start`, or null. Longest wins because a
// question can offer both "Wellington" and "Wellington, Newtown Special
// Administrative Region" — the shorter label is a legitimate prefix of the
// longer one, and taking it would leave the remainder as unmatched rubbish.
// Indexed rather than given a sliced string: slicing the remainder at every step
// copies the rest of the answer each time, which is quadratic in its length.
function matchOptionAt(
  text: string,
  start: number,
  options: string[],
): string | null {
  let longest: string | null = null
  for (const option of options) {
    if (!option || option.length <= (longest?.length ?? 0)) continue
    const end = start + option.length
    if (text.slice(start, end).toLowerCase() !== option.toLowerCase()) continue
    if (endsAtDelimiter(text, end)) longest = option
  }
  return longest
}

// The option a whole string names, ignoring case, or null. Returns the label in
// the casing the question defines so a selection is recorded canonically.
function findOption(text: string, options: string[]): string | null {
  const lowered = text.toLowerCase()
  return options.find((option) => option.toLowerCase() === lowered) ?? null
}

// Walk one line, consuming known option labels whole. Option labels are matched
// against the raw text rather than reassembled from comma fragments because the
// comma inside a label is not always followed by a space ("$21,000 - $50,000"),
// so a fragment rejoin cannot reproduce the original label. Text that matches no
// option is emitted as-is, up to the next comma, which keeps genuinely invalid
// selections invalid rather than silently absorbing them.
function parseLine(line: string, options: string[]): string[] {
  const selections: string[] = []
  const end = line.length
  let i = 0

  const skipSpace = () => {
    while (i < end && WHITESPACE.test(line[i])) i += 1
  }

  skipSpace()
  // A leading "-"/"•" is only a bullet when keeping it fails to produce a known
  // label: an option can itself begin with "-", such as a negative range like
  // "-20% to 0%", and stripping that makes it unmatchable.
  if (i < end && BULLETS.includes(line[i]) && !matchOptionAt(line, i, options)) {
    // A bullet is written by the save path, one per selection, so the rest of
    // the line is a single selection whatever punctuation it holds. Splitting
    // inside it can only misread a label the question no longer offers: as
    // fragments in the error rather than the label the user chose, or as a tick
    // on a shorter option that happens to be a prefix of it.
    const selection = line.slice(i + 1).trim()
    if (!selection) return []
    return [findOption(selection, options) ?? selection]
  }

  for (;;) {
    skipSpace()
    if (i >= end) break

    const option = matchOptionAt(line, i, options)
    if (option) {
      selections.push(option)
      i += option.length
    } else {
      const comma = line.indexOf(',', i)
      const stop = comma === -1 ? end : comma
      const fragment = line.slice(i, stop).trim()
      if (fragment) selections.push(fragment)
      i = stop
    }

    // consume one delimiting comma and the space around it
    skipSpace()
    if (i < end && line[i] === ',') i += 1
  }
  return selections
}

// Newlines are split first and unconditionally: no option label contains one, so
// a newline is always a delimiter, whereas a comma is only a delimiter when it
// falls outside a label.
function parseOptionsPart(optionsPart: string, options: string[]): string[] {
  if (!options.length) {
    return optionsPart
      .split(/[,\n]+/)
      .map((s) => stripLeading(s).trim())
      .filter(Boolean)
  }
  return optionsPart.split('\n').flatMap((line) => parseLine(line, options))
}

// Free-text "Other" answers can themselves contain commas/newlines, so the
// "Other: <text>" chunk is always kept last and split off whole rather than
// treated as more comma-separated options. Returns the option selections
// (excluding the Other chunk) plus the free "Other" text (null when there is
// no "Other: " chunk).
//
// Pass the question's option labels so labels containing commas survive the
// split. Without them a comma is treated as a delimiter everywhere, which
// shreds such a label into fragments that match no option. Passing them also
// re-heals an answer that was already shredded, so an edit session in progress
// recovers instead of needing the answer regenerated. Duplicate selections are
// collapsed — a multi_choice answer is a set, and a shredded answer that was
// re-ticked several times carries repeats.
export function splitMultiChoiceAnswer(
  answer: string,
  options: string[] = [],
): {
  selections: string[]
  otherText: string | null
} {
  const otherIndex = answer.indexOf(OTHER_PREFIX)
  const optionsPart = otherIndex === -1 ? answer : answer.slice(0, otherIndex)
  const otherText =
    otherIndex === -1
      ? null
      : answer.slice(otherIndex + OTHER_PREFIX.length).trim()
  const selections = [...new Set(parseOptionsPart(optionsPart, options))]
  return { selections, otherText }
}

// Rebuild the comma-joined multi_choice working string from option selections
// and the free "Other" text. The Other chunk (free text, or a bare "Other"
// when no text has been entered yet) is always placed last. Pass otherText as
// null when "Other" is not selected, or '' for a bare "Other" selection.
export function buildMultiChoiceAnswer(
  selections: string[],
  otherText: string | null,
): string {
  const options = selections.filter(
    (s) => s !== 'Other' && !s.startsWith(OTHER_PREFIX),
  )
  if (otherText === null) {
    return options.join(', ')
  }
  return [...options, otherText ? `${OTHER_PREFIX}${otherText}` : 'Other'].join(
    ', ',
  )
}
